import { HttpErrorResponse } from '@angular/common/http'
import { ErrorHandler, inject, Injectable, LOCALE_ID, NgZone } from '@angular/core'
import { Router } from '@angular/router'

import { MessageService } from 'primeng/api'

import * as Sentry from '@sentry/angular'
import { SentryErrorHandler } from '@sentry/angular'

import { UserService } from '@app/services/user.service'

/**
 * Keep only what identifies the user in Sentry, plus their roles to help triage: names, permissions and the
 * session token have no use there and are personal data we would rather not ship to a third party.
 */
export function toSentryUser (user: { id?: number, email: string, username: string, roles: readonly string[] }): Sentry.User {
  return { id: user.id, email: user.email, username: user.username, roles: user.roles }
}

// names pdf.js gives the exceptions it raises for unreadable PDF content
const PDF_JS_ERROR_NAMES = new Set(['UnknownErrorException', 'InvalidPDFException', 'FormatError'])

/**
 * pdf.js errors are rebuilt from the worker thread, so match them by name rather than by class
 */
export function isPdfJsError (error: unknown): boolean {
  return error instanceof Error && PDF_JS_ERROR_NAMES.has(error.name)
}

/**
 * Intercepts all errors, JS or HTTP and provides a toast to the user.
 *
 * This can be avoided for specific HTTP calls, for example via the following pattern:
 *
 *    this.myApi.retrieve(code).subscribe(
        (res: any) => {
          this.detail = res
        },
        (error: HttpErrorResponse) => {
          // swallow 404 errors for this case
          if (error.status === 404) return
          // non-404 errors raise to the generic error handler
          throw error
        })
  **/
@Injectable({
  providedIn: 'root',
})
export class GlobalErrorInterceptor implements ErrorHandler {
  private readonly messageService = inject(MessageService)
  private readonly userService = inject(UserService)
  private readonly router = inject(Router)
  private readonly zone = inject(NgZone)
  private readonly locale = inject(LOCALE_ID)

  private readonly sentryErrorHandler: SentryErrorHandler

  constructor () {
    Sentry.setTag('locale', this.locale)
    this.userService.getUser().subscribe({
      next: user => {
        if (user.anonymous) {
          Sentry.setUser(null)
        } else {
          Sentry.setUser(toSentryUser(user))
        }
      },
    })

    this.sentryErrorHandler = Sentry.createErrorHandler({
      showDialog: false,
      logErrors: false,
    })
  }

  handleError (error: any): void {
    error = this.getOriginalError(error)
    console.log(error.message ?? error)

    // ngx-extended-pdf-viewer leaves some pdf.js page loads un-awaited, so a damaged PDF can land here:
    //   the viewer already checks its pages and reports a damaged PDF, so only keep it in the console
    if (isPdfJsError(error)) {
      console.error(error)
      return
    }

    this.sentryErrorHandler.handleError(error)

    this.zone.run(() => {
      this.getErrorMessages(error).forEach(errorMessage => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage })
      })

      // NG04002: Cannot match any routes → go to home page
      // @ts-expect-error Some angular errors have a code
      if (error instanceof Error && error.code === 4002) {
        this.router.navigate([])
      }

      console.error(error)
    })
  }

  getErrorMessages (error: any): string[] {
    let errorMessages: string[] = []

    if (error instanceof Error) {
      // msal-browser v5 replaces error.message with a bare docs link, so prefer the stable errorCode
      errorMessages.push((error as { errorCode?: string }).errorCode ?? error.message)
    }
    if (error.error instanceof ErrorEvent) {
      errorMessages.push(error.error.message)
    }
    if (error instanceof HttpErrorResponse) {
      errorMessages = this.getHttpErrorResponseMessages(error)
    }

    return errorMessages.length === 0 ? ['unknown error'] : errorMessages
  }

  /**
   * Extract a list of error messages to display to the user from the incoming Error object
   */
  private getHttpErrorResponseMessages (error: HttpErrorResponse): string[] {
    const errorMessages: string[] = []
    if (error.status === 0) {
      // no http response :|
      errorMessages.push('Error contacting backend')
    } else if (error.error && typeof error.error === 'object') {
      // otherwise a DRF dictionary-style error payload
      Object.keys(error.error).forEach(key =>
        errorMessages.push(`${key}: ${error.error[key]}`),
      )
    } else if (error.message) {
      // otherwise a bare message string
      errorMessages.push(error.message)
    }
    return errorMessages
  }

  private getOriginalError (error: any): any {
    let e = error
    if (error.rejection !== undefined) {
      e = error.rejection
    }
    return e === error ? error : this.getOriginalError(e)
  }
}
